import { Visibility, VisibilityOff } from '@mui/icons-material';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import SportsEsportsOutlinedIcon from '@mui/icons-material/SportsEsportsOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Container,
    FormControl,
    FormControlLabel,
    FormGroup,
    Grid,
    IconButton,
    InputAdornment,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    Switch,
    Typography
} from '@mui/material';
import { Confirm, Report } from 'notiflix';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { ROLE_IDS, isAdmin, isCompany } from '../../../../config/roles';
import { getRoles } from '../../../../data/api/roles/RoleRoutes';
import { getAllUsers, updateUser } from '../../../../data/api/users/UserRoutes';
import useIndex from '../../../../hooks/useIndex';
import validErrors from '../../../../hooks/validErrors';

const fieldStyles = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '14px',
        background: '#fff',
        color: '#10285f',
        fontWeight: 700,

        '& fieldset': {
            borderColor: 'rgba(15, 34, 77, 0.16)',
        },

        '&:hover fieldset': {
            borderColor: 'rgba(214, 170, 58, 0.7)',
        },

        '&.Mui-focused fieldset': {
            borderColor: '#d6aa3a',
            borderWidth: '2px',
        },
    },

    '& .MuiInputLabel-root': {
        color: 'rgba(15, 34, 77, 0.65)',
        fontWeight: 700,
    },

    '& .MuiInputLabel-root.Mui-focused': {
        color: '#10285f',
    },
};

const UserConfig = ({ user, loadData }) => {
    const { auth, token } = useIndex();

    const admin = isAdmin(auth);
    const empresa = isCompany(auth);

    const { register, handleSubmit, control, watch, reset } = useForm({
        defaultValues: {
            role_id: user?.role_id ?? '',
            company_id: user?.company_id ?? '',
            password: '',
        },
    });

    const [showPassword, setShowPassword] = useState(false);
    const [roles, setRoles] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [load, setLoad] = useState(false);

    const selectedRoleId = Number(watch('role_id'));
    const showCompanySelector = admin && selectedRoleId === ROLE_IDS.DISTRIBUIDOR;
    const editingDistributorAsCompany = empresa && auth?.id !== user?.id;

    const handleClickShowPassword = () => setShowPassword((show) => !show);

    useEffect(() => {
        if (admin) loadRoles();
    }, [admin]);

    useEffect(() => {
        if (admin && showCompanySelector && companies.length === 0) {
            loadCompanies();
        }
    }, [admin, showCompanySelector]);

    const loadRoles = async () => {
        try {
            const { data } = await getRoles(token);
            setRoles(data?.data ?? []);
        } catch (error) {
            validErrors(error);
        }
    };

    const loadCompanies = async () => {
        try {
            const { data } = await getAllUsers(token, 100, 1, '', {
                role_id: ROLE_IDS.EMPRESA,
            });
            setCompanies(data?.data ?? []);
        } catch (error) {
            validErrors(error);
        }
    };

    const sendUserData = async (dataSend) => {
        setLoad(true);

        try {
            const { data } = await updateUser(token, user?.uuid, dataSend);

            if (data?.success) {
                Report.success(
                    'Éxito',
                    data?.message,
                    'Okay',
                    { textAlign: 'center' }
                );

                reset({
                    role_id: user?.role_id ?? '',
                    company_id: user?.company_id ?? '',
                    password: '',
                });

                loadData();
            }
        } catch (error) {
            validErrors(error);
        } finally {
            setLoad(false);
        }
    };

    /**
     * Acción rápida del admin para conmutar el estado del usuario:
     * - Si está activo: lo deshabilita (status = 0, game_enabled = 0).
     * - Si está deshabilitado: lo habilita (status = 1, game_enabled = 1).
     *
     * No la dejamos como atajo silencioso — pedimos confirmación porque
     * en ambos sentidos el cambio toca acceso al sistema y permiso de juego.
     */
    const isSelfProfile = auth?.id === user?.id;
    const isUserDisabled = Number(user?.status) === 0;
    const canToggleUserAccess = admin && !isSelfProfile;
    // Si el usuario está deshabilitado bloqueamos toda edición de datos:
    // la única acción posible es reactivarlo desde la card inferior. Esto
    // evita que un admin "guarde cambios" sobre alguien que no debería
    // estar operativo en el sistema.
    const formDisabled = isUserDisabled;

    const handleToggleUserAccess = () => {
        const willDisable = !isUserDisabled;
        const title = willDisable ? 'Deshabilitar usuario' : 'Habilitar usuario';
        const message = willDisable
            ? `¿Seguro que deseas deshabilitar a ${user?.name ?? 'este usuario'}? No podrá acceder al sistema ni jugar hasta que lo reactives.`
            : `¿Seguro que deseas habilitar a ${user?.name ?? 'este usuario'}? Recuperará acceso al sistema y permiso para jugar.`;
        const confirmLabel = willDisable ? 'Sí, deshabilitar' : 'Sí, habilitar';

        Confirm.show(
            title,
            message,
            confirmLabel,
            'Cancelar',
            () => sendUserData({
                name: user?.name,
                lastname: user?.lastname,
                email: user?.email,
                phone: user?.phone,
                document: user?.document,
                document_type_id: user?.document_type_id,
                role_id: user?.role_id,
                company_id: user?.company_id ?? null,
                status: willDisable ? 0 : 1,
                game_enabled: willDisable ? 0 : 1,
            }),
            () => {},
        );
    };

    const onSubmit = async (dataForm) => {
        const payload = {
            name: user?.name,
            lastname: user?.lastname,
            email: user?.email,
            phone: dataForm.phone ?? user?.phone,
            document: user?.document,
            document_type_id: user?.document_type_id,
        };

        if (admin) {
            payload.role_id = Number(dataForm.role_id);
            payload.status = dataForm.status ? 1 : 0;
            payload.game_enabled = dataForm.game_enabled ? 1 : 0;

            if (Number(dataForm.role_id) === ROLE_IDS.DISTRIBUIDOR) {
                payload.company_id = dataForm.company_id
                    ? Number(dataForm.company_id)
                    : null;
            } else {
                payload.company_id = null;
            }

            if (dataForm.password && dataForm.password.trim() !== '') {
                payload.password = dataForm.password;
                payload.password_confirmation = dataForm.password;
            }
        } else if (empresa && auth?.id !== user?.id) {
            payload.name = dataForm.name ?? user?.name;
            payload.lastname = dataForm.lastname ?? user?.lastname;
            payload.status = dataForm.status ? 1 : 0;
        } else {
            if (dataForm.password && dataForm.password.trim() !== '') {
                payload.password = dataForm.password;
                payload.password_confirmation = dataForm.password;
            }
        }

        Confirm.show(
            'Confirmar cambios',
            '¿Estás seguro de realizar estos cambios?',
            'Sí',
            'No',
            () => sendUserData(payload),
            () => { }
        );
    };

    const userInfo = [
        {
            label: 'Nombre',
            value: `${user?.name ?? ''} ${user?.lastname ?? ''}`.trim() || '-',
            icon: <PersonOutlineOutlinedIcon />,
        },
        {
            label: 'Correo',
            value: user?.email ?? '-',
            icon: <BadgeOutlinedIcon />,
        },
        {
            label: 'Teléfono',
            value: user?.phone ?? '-',
            icon: <BadgeOutlinedIcon />,
        },
        {
            label: user?.document_type_short ?? 'Documento',
            value: user?.document ?? '-',
            icon: <BadgeOutlinedIcon />,
        },
        {
            label: 'Jugadas',
            value: user?.moves ?? 0,
            icon: <SportsEsportsOutlinedIcon />,
        },
        {
            label: 'Rol',
            value: user?.role ?? '-',
            icon: <ManageAccountsOutlinedIcon />,
        },
        ...(user?.company
            ? [{
                label: 'Empresa asociada',
                value: `${user?.company?.name ?? ''} ${user?.company?.lastname ?? ''}`.trim(),
                icon: <BusinessOutlinedIcon />,
            }]
            : []),
        {
            label: 'Estado',
            value: user?.status_name ?? '-',
            icon: <ManageAccountsOutlinedIcon />,
        },
        {
            label: 'Habilitado para jugar',
            value: user?.game_enabled_name ?? '-',
            icon: <SportsEsportsOutlinedIcon />,
        },
    ];

    return (
        <Wrapper>
            <Header>
                <Typography className="title">
                    Configuración
                </Typography>

                <Typography className="subtitle">
                    Consulta la información del usuario y modifica permisos, estado o contraseña.
                </Typography>
            </Header>

            <InfoCard>
                <SectionTitle>
                    <PersonOutlineOutlinedIcon />
                    <Box>
                        <Typography className="section-title">
                            Datos de usuario
                        </Typography>
                        <Typography className="section-subtitle">
                            Información actual registrada en el sistema
                        </Typography>
                    </Box>
                </SectionTitle>

                <InfoGrid>
                    {userInfo.map((item) => (
                        <InfoItem key={item.label}>
                            <Box className="icon">
                                {item.icon}
                            </Box>

                            <Box className="info">
                                <span>{item.label}</span>
                                <strong>{item.value}</strong>
                            </Box>
                        </InfoItem>
                    ))}
                </InfoGrid>
            </InfoCard>

            <StyledDivider>
                <span>Modificar información</span>
            </StyledDivider>

            <form onSubmit={handleSubmit(onSubmit)}>
                <ConfigCard>
                    <SectionTitle>
                        <ManageAccountsOutlinedIcon />
                        <Box>
                            <Typography className="section-title">
                                Ajustes del usuario
                            </Typography>
                            <Typography className="section-subtitle">
                                {formDisabled
                                    ? 'Usuario deshabilitado: los campos están bloqueados. Habilítalo desde la sección inferior para editar.'
                                    : 'Cambia la configuración permitida para este perfil'}
                            </Typography>
                        </Box>
                    </SectionTitle>

                    <Grid container spacing={2}>
                        {editingDistributorAsCompany && (
                            <>
                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth sx={fieldStyles} disabled={formDisabled}>
                                        <OutlinedInput
                                            defaultValue={user?.name ?? ''}
                                            placeholder="Nombre"
                                            size="small"
                                            disabled={formDisabled}
                                            {...register('name')}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth sx={fieldStyles} disabled={formDisabled}>
                                        <OutlinedInput
                                            defaultValue={user?.lastname ?? ''}
                                            placeholder="Apellido"
                                            size="small"
                                            disabled={formDisabled}
                                            {...register('lastname')}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <FormControl fullWidth sx={fieldStyles} disabled={formDisabled}>
                                        <OutlinedInput
                                            defaultValue={user?.phone ?? ''}
                                            placeholder="Teléfono"
                                            type="number"
                                            size="small"
                                            disabled={formDisabled}
                                            {...register('phone')}
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12}>
                                    <SwitchGroup>
                                        <StyledControlLabel
                                            control={
                                                <Switch
                                                    defaultChecked={!!user?.status}
                                                    disabled={formDisabled}
                                                    {...register('status')}
                                                />
                                            }
                                            label={<Typography>Puede acceder</Typography>}
                                        />
                                    </SwitchGroup>
                                </Grid>
                            </>
                        )}

                        {admin && (
                            <>
                                <Grid item xs={12} md={showCompanySelector ? 3 : 4}>
                                    <Controller
                                        name="role_id"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl fullWidth size="small" sx={fieldStyles} disabled={formDisabled}>
                                                <InputLabel id="user_role_label">Rol</InputLabel>
                                                <Select
                                                    {...field}
                                                    labelId="user_role_label"
                                                    label="Rol"
                                                    value={field.value ?? ''}
                                                    disabled={formDisabled}
                                                >
                                                    {roles.map((item) => (
                                                        <MenuItem key={item?.id} value={item?.id}>
                                                            {item?.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                </Grid>

                                {showCompanySelector && (
                                    <Grid item xs={12} md={3}>
                                        <Controller
                                            name="company_id"
                                            control={control}
                                            render={({ field }) => (
                                                <FormControl fullWidth size="small" sx={fieldStyles} disabled={formDisabled}>
                                                    <InputLabel id="user_company_label">
                                                        Empresa asociada
                                                    </InputLabel>
                                                    <Select
                                                        {...field}
                                                        labelId="user_company_label"
                                                        label="Empresa asociada"
                                                        value={field.value ?? ''}
                                                        disabled={formDisabled}
                                                    >
                                                        {companies.map((c) => (
                                                            <MenuItem key={c.id} value={c.id}>
                                                                {c.name} {c.lastname}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                </FormControl>
                                            )}
                                        />
                                    </Grid>
                                )}

                                <Grid item xs={12} md={showCompanySelector ? 3 : 4}>
                                    <FormControl
                                        fullWidth
                                        size="small"
                                        variant="outlined"
                                        sx={fieldStyles}
                                        disabled={formDisabled}
                                    >
                                        <InputLabel htmlFor="password">Contraseña</InputLabel>
                                        <OutlinedInput
                                            id="password"
                                            type={showPassword ? 'text' : 'password'}
                                            placeholder="Contraseña"
                                            label="Contraseña"
                                            disabled={formDisabled}
                                            {...register('password')}
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        onClick={handleClickShowPassword}
                                                        edge="end"
                                                        size="small"
                                                        disabled={formDisabled}
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            }
                                        />
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={showCompanySelector ? 3 : 4}>
                                    <SwitchGroup>
                                        <StyledControlLabel
                                            control={
                                                <Switch
                                                    defaultChecked={!!user?.status}
                                                    disabled={formDisabled}
                                                    {...register('status')}
                                                />
                                            }
                                            label={<Typography>Puede acceder</Typography>}
                                        />

                                        <StyledControlLabel
                                            control={
                                                <Switch
                                                    defaultChecked={!!user?.game_enabled}
                                                    disabled={formDisabled}
                                                    {...register('game_enabled')}
                                                />
                                            }
                                            label={<Typography>Puede jugar</Typography>}
                                        />
                                    </SwitchGroup>
                                </Grid>
                            </>
                        )}

                        {!admin && !editingDistributorAsCompany && (
                            <Grid item xs={12} md={6}>
                                <FormControl
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    sx={fieldStyles}
                                    disabled={formDisabled}
                                >
                                    <InputLabel htmlFor="password">Contraseña</InputLabel>
                                    <OutlinedInput
                                        id="password"
                                        type={showPassword ? 'text' : 'password'}
                                        placeholder="Contraseña"
                                        label="Contraseña"
                                        disabled={formDisabled}
                                        {...register('password')}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={handleClickShowPassword}
                                                    edge="end"
                                                    size="small"
                                                    disabled={formDisabled}
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                    />
                                </FormControl>
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <LoadingButton
                                fullWidth
                                loading={load}
                                loadingPosition="start"
                                startIcon={<i className="fa-solid fa-floppy-disk"></i>}
                                variant="contained"
                                size="small"
                                type="submit"
                                disabled={formDisabled}
                                sx={{
                                    borderRadius: '14px',
                                    py: 1.1,
                                    fontWeight: 900,
                                    textTransform: 'uppercase',
                                    background: 'linear-gradient(90deg, #10285f 0%, #1a2f5c 100%)',
                                    boxShadow: '0 6px 14px rgba(15, 34, 77, 0.18)',
                                    '&:hover': {
                                        background: 'linear-gradient(90deg, #1a2f5c 0%, #10285f 100%)',
                                    },
                                }}
                            >
                                <span>{formDisabled ? 'Usuario deshabilitado' : 'Guardar cambios'}</span>
                            </LoadingButton>
                        </Grid>
                    </Grid>
                </ConfigCard>
            </form>

            {canToggleUserAccess && (
                <DangerZone $disabled={isUserDisabled}>
                    <Box className="danger-info">
                        <Box className="danger-icon">
                            {isUserDisabled
                                ? <CheckCircleOutlineIcon />
                                : <WarningAmberOutlinedIcon />}
                        </Box>
                        <Box>
                            <Typography className="danger-title">
                                {isUserDisabled ? 'Habilitar usuario' : 'Deshabilitar usuario'}
                            </Typography>
                            <Typography className="danger-text">
                                {isUserDisabled
                                    ? 'Restablece el acceso al sistema y el permiso para jugar.'
                                    : 'Desactiva el acceso al sistema y la posibilidad de jugar. Podés revertirlo desde aquí mismo.'}
                            </Typography>
                        </Box>
                    </Box>

                    <LoadingButton
                        loading={load}
                        loadingPosition="start"
                        startIcon={isUserDisabled ? <CheckCircleOutlineIcon /> : <BlockOutlinedIcon />}
                        variant="contained"
                        color={isUserDisabled ? 'success' : 'error'}
                        onClick={handleToggleUserAccess}
                        sx={{
                            borderRadius: '12px',
                            px: 2,
                            py: 1,
                            fontWeight: 900,
                            textTransform: 'uppercase',
                        }}
                    >
                        <span>{isUserDisabled ? 'Habilitar' : 'Deshabilitar'}</span>
                    </LoadingButton>
                </DangerZone>
            )}
        </Wrapper>
    );
};

export default UserConfig;

const Wrapper = styled(Container)`
    padding: 0 !important;
    max-width: 100% !important;
`;

const Header = styled.div`
    margin-bottom: 1rem;

    .title {
        color: #10285f;
        font-size: 1.25rem;
        font-weight: 900;
    }

    .subtitle {
        margin-top: 0.2rem;
        color: rgba(15, 34, 77, 0.62);
        font-size: 0.9rem;
        font-weight: 600;
    }
`;

const InfoCard = styled.div`
    padding: 1rem;
    margin-bottom: 1rem;
    border-radius: 20px;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(214, 170, 58, 0.25);
    box-shadow: 0 10px 22px rgba(15, 34, 77, 0.06);
`;

const ConfigCard = styled.div`
    padding: 1rem;
    border-radius: 20px;
    background: linear-gradient(180deg, #ffffff 0%, #f6f8ff 100%);
    border: 1px solid rgba(15, 34, 77, 0.1);
    box-shadow: 0 10px 22px rgba(15, 34, 77, 0.06);
`;

const SectionTitle = styled.div`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;

    svg {
        color: #d6aa3a;
        font-size: 1.45rem;
        flex-shrink: 0;
    }

    .section-title {
        color: #10285f;
        font-size: 1rem;
        font-weight: 900;
        line-height: 1.2;
    }

    .section-subtitle {
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.8rem;
        font-weight: 600;
        margin-top: 0.1rem;
    }
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;

    @media (max-width: 700px) {
        grid-template-columns: 1fr;
    }
`;

const InfoItem = styled.div`
    display: flex;
    gap: 0.75rem;
    align-items: center;
    padding: 0.75rem;
    border-radius: 16px;
    background: rgba(16, 40, 95, 0.045);
    border: 1px solid rgba(15, 34, 77, 0.08);
    min-width: 0;

    .icon {
        width: 38px;
        height: 38px;
        border-radius: 13px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #10285f;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
        flex-shrink: 0;

        svg {
            font-size: 1.15rem;
        }
    }

    .info {
        min-width: 0;
    }

    span {
        display: block;
        color: rgba(15, 34, 77, 0.58);
        font-size: 0.72rem;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.35px;
    }

    strong {
        display: block;
        margin-top: 0.1rem;
        color: #10285f;
        font-size: 0.9rem;
        font-weight: 900;
        word-break: break-word;
    }
`;

const StyledDivider = styled.div`
    margin: 1rem 0;
    display: flex;
    align-items: center;
    gap: 1rem;
    color: #10285f;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.5px;

    &::before,
    &::after {
        content: "";
        flex: 1;
        height: 1px;
        background: rgba(15, 34, 77, 0.13);
    }

    span {
        padding: 0.35rem 0.9rem;
        border-radius: 999px;
        background: rgba(214, 170, 58, 0.16);
        border: 1px solid rgba(214, 170, 58, 0.28);
    }
`;

const SwitchGroup = styled(FormGroup)`
    padding: 0.55rem 0.75rem;
    border-radius: 14px;
    background: rgba(16, 40, 95, 0.035);
    border: 1px solid rgba(15, 34, 77, 0.08);
`;

const StyledControlLabel = styled(FormControlLabel)`
    margin-left: 0 !important;

    .MuiTypography-root {
        color: #10285f;
        font-size: 0.88rem;
        font-weight: 800;
    }

    .MuiSwitch-switchBase.Mui-checked {
        color: #d6aa3a;
    }

    .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track {
        background-color: #d6aa3a;
    }
`;

const DangerZone = styled.div`
    /* La card es destructiva (rojo) cuando vamos a deshabilitar y
       constructiva (verde) cuando vamos a habilitar al usuario. */
    --accent: ${({ $disabled }) => ($disabled ? '#2e7d32' : '#b71c1c')};
    --accent-strong: ${({ $disabled }) => ($disabled ? 'rgba(46, 125, 50, 0.32)' : 'rgba(211, 47, 47, 0.32)')};
    --accent-soft: ${({ $disabled }) => ($disabled ? 'rgba(46, 125, 50, 0.06)' : 'rgba(211, 47, 47, 0.06)')};
    --accent-icon-bg: ${({ $disabled }) => ($disabled ? 'rgba(46, 125, 50, 0.12)' : 'rgba(211, 47, 47, 0.12)')};
    --accent-icon-border: ${({ $disabled }) => ($disabled ? 'rgba(46, 125, 50, 0.25)' : 'rgba(211, 47, 47, 0.25)')};

    margin-top: 1rem;
    padding: 1rem 1.1rem;
    border-radius: 18px;
    border: 1px solid var(--accent-strong);
    background: var(--accent-soft);
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    flex-wrap: wrap;

    .danger-info {
        display: flex;
        align-items: center;
        gap: 0.85rem;
        min-width: 0;
    }

    .danger-icon {
        width: 42px;
        height: 42px;
        border-radius: 12px;
        background: var(--accent-icon-bg);
        border: 1px solid var(--accent-icon-border);
        color: var(--accent);
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .danger-title {
        color: var(--accent);
        font-weight: 900;
        font-size: 0.98rem;
        line-height: 1.1;
    }

    .danger-text {
        margin-top: 0.15rem;
        color: rgba(15, 34, 77, 0.7);
        font-size: 0.82rem;
        font-weight: 600;
    }

    @media (max-width: 600px) {
        flex-direction: column;
        align-items: stretch;

        .MuiButton-root {
            width: 100%;
        }
    }
`;